
import AudioContextProvider from '../audio/AudioContextProvider'

interface AudioStreamerCallbacks {
  onError: (error: string) => void
  onSpeechStarted?: () => void
  onSpeechEnded?: () => void
  onAudioChunk: (audioBase64Chunk: string) => void
}

class AudioStreamer {
  private audioContext: AudioContext | null = null
  private workletNode: AudioWorkletNode | null = null
  private userStream: MediaStream
  private callbacks: AudioStreamerCallbacks
  private audioBufferQueue: Int16Array = new Int16Array(0)
  private sampleRate: number

  constructor(userStream: MediaStream, sampleRate: number, callbacks: AudioStreamerCallbacks) {
    this.userStream = userStream
    this.sampleRate = sampleRate
    this.callbacks = callbacks
  }

  public async start(): Promise<void> {
    try {
      await this.initOptimizedAudioProcessing()
    } catch (error) {
      this.callbacks.onError(error instanceof Error ? error.message : 'Unknown error')
      this.stop()
    }
  }

  private hasAudioContent = (audioBuffer: Int16Array): boolean => {
    if (!audioBuffer || audioBuffer.length === 0) {
      return false
    }

    const silenceThreshold = 10 // Adjust based on your needs

    for (let i = 0; i < audioBuffer.length; i++) {
      if (Math.abs(audioBuffer[i]) > silenceThreshold) {
        return true
      }
    }

    return false // All silence
  }

  private async initOptimizedAudioProcessing(): Promise<void> {
    // Use optimal sample rate for OpenAI
    this.audioContext = AudioContextProvider.getAudioContext(this.sampleRate)
    this.audioContext.resume()
    const source = this.audioContext.createMediaStreamSource(this.userStream)

    await this.audioContext.audioWorklet.addModule('/audio-processor.js')
    this.workletNode = new AudioWorkletNode(this.audioContext, 'audio-processor')

    source.connect(this.workletNode)
    this.workletNode.connect(this.audioContext.destination)

    this.workletNode.port.onmessage = (event) => {
      if(event.data.volume >= 0.2) {
        if(this.callbacks.onSpeechStarted) { this.callbacks.onSpeechStarted() }
      } else {
        if(this.callbacks.onSpeechEnded) { this.callbacks.onSpeechEnded() }
      }
      const currentBuffer = new Int16Array(event.data.audioPcm16)
      this.audioBufferQueue = this.mergeBuffers(this.audioBufferQueue, currentBuffer)

      if (!this.audioContext) {
        throw new Error('AudioContext is not initialized')
      }

      // Optimized: Send smaller chunks more frequently (50ms instead of 100ms)
      const chunkDurationMs = 50
      const chunkSamples = Math.floor(this.audioContext.sampleRate * (chunkDurationMs / 1000))

      while (this.audioBufferQueue.length >= chunkSamples) {
        const chunk = this.audioBufferQueue.subarray(0, chunkSamples)
        this.audioBufferQueue = this.audioBufferQueue.subarray(chunkSamples)
        this.appendAudioBuffer(chunk)
      }
    }
  }

  private mergeBuffers(lhs: Int16Array, rhs: Int16Array): Int16Array {
    const mergedBuffer = new Int16Array(lhs.length + rhs.length)
    mergedBuffer.set(lhs, 0)
    mergedBuffer.set(rhs, lhs.length)
    return mergedBuffer
  }

  private encodeInt16ArrayToBase64(int16Array: Int16Array): string {
    // Optimized encoding - pre-allocate buffer
    const buffer = new ArrayBuffer(int16Array.length * 2)
    const view = new DataView(buffer)

    for (let i = 0; i < int16Array.length; i++) {
      view.setInt16(i * 2, int16Array[i], true)
    }

    const uint8Array = new Uint8Array(buffer)
    const binary = String.fromCharCode(...uint8Array)
    return btoa(binary)
  }

  private appendAudioBuffer(audioBuffer: Int16Array): void {
    if(this.hasAudioContent(audioBuffer)) {
      const audioEncoded = this.encodeInt16ArrayToBase64(audioBuffer)

      this.callbacks.onAudioChunk(audioEncoded)
    }
  }

  private async flushRemainingAudio(): Promise<void> {
    return new Promise((resolve) => {
      if (this.audioBufferQueue.length > 0) {
        console.log(`📤 Flushing ${this.audioBufferQueue.length} remaining samples`)
        this.appendAudioBuffer(this.audioBufferQueue)
        this.audioBufferQueue = new Int16Array(0)
      }
      resolve()
    })
  }

  private async finishGracefully(): Promise<void> {
    return this.cleanup()
  }

  private async cleanup(): Promise<void> {
    return new Promise(resolve => {
      setTimeout(async () => {
        if (this.audioContext) {
          // Suspend to allow reuse within the session
          await AudioContextProvider.suspend(this.sampleRate)
        }
        this.workletNode?.disconnect()
        this.workletNode = null
        resolve()
      }, 200)
    })
  }

  public discard() {
    this.cleanup()
  }

  public async stop(): Promise<void> {
    console.log('⏹️ Stopping recording...')
    this.flushRemainingAudio()
    return this.finishGracefully()
  }
}

export default AudioStreamer
